// Pure formatting helpers for the mobile home cards (kept out of the .tsx card files so fast-refresh stays clean).

/** The em-dash a born-blank money/number figure renders as (never a fabricated 0).*/
export const DEGRADED = "—";

/**
 * Whole-dollar USD (no cents). `usd(0)` → `"$0"` — a MEASURED zero, which is the connected-zero state; a
 * figure we do not hold → the em-dash.
 *
 * IT USED TO COERCE `cents ?? 0` while its own comment claimed it matched the desktop merch card, which it
 * had stopped doing: `MerchRollupCard.usd` was fixed for exactly this in audit 12.10 and this copy was not,
 * so every mobile merch figure we lacked printed `$0`. That is the case 12.10 named — Lifetime is null
 * until the token carries `read_all_orders`, and a scope we do not hold is not revenue of zero — plus, since
 * ART-138, a POS figure the vendor cannot split. `num` directly below always had the rule right ("never a
 * fabricated 0 on a home KPI"); the two sat three lines apart disagreeing.
 *
 * The settlement callers guard `!= null ? usd(x) : DEGRADED` themselves, so this only ever changes a figure
 * that was being fabricated.
 */
export function usd(cents: number | null | undefined): string {
  if (cents == null) return DEGRADED;
  return (cents / 100).toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
}

/** Compact integer (1234 → "1,234"); null/undefined → the em-dash (never a fabricated 0 on a home KPI). */
export function num(n: number | null | undefined): string {
  return n == null ? DEGRADED : n.toLocaleString();
}

/** Today as an ISO date (`YYYY-MM-DD`) — the lower bound for "upcoming" show queries so the soonest FUTURE show
 * (not the oldest confirmed show in history) is items[0]. Stable within a day ⇒ a stable React Query key.
 *
 * The DEVICE's local date, never UTC's: a show date is a wall-clock date, and `toISOString()` rolls over at UTC
 * midnight — 8 PM Eastern — so every evening the query started at TOMORROW, dropped tonight's show, and the home
 * greeted a band on its show day with "Next show in 338 days". */
export function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** `2026-08-05` → `Aug 5` (the home cards show short dates; time zones never matter for a date-only field). */
export function shortDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/** `2026-09-24` → `Thu, Sep 24` — the Travel row's meta line (weekday + short date). Local wall-clock like
 *  `shortDate`, never a UTC shift on a date-only field. */
export function weekdayDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}

/** A flight's departure in 12-hour time — `2026-09-24T07:05:00` → `7:05 AM`. The stored HH:MM is read off the
 *  string directly (never `new Date(...).getHours()`), so a leg's local departure time renders as recorded
 *  rather than being shifted into the viewer's zone. */
export function time12(dt: string | null | undefined): string {
  const m = /T(\d{2}):(\d{2})/.exec(dt ?? "");
  if (!m) return "";
  let h = Number(m[1]);
  const min = m[2];
  const ampm = h < 12 ? "AM" : "PM";
  h = h % 12 || 12;
  return `${h}:${min} ${ampm}`;
}

/** A hotel's stay range — `Sep 24 → Sep 26`. A single side renders the other against `?` (`Oct 2 → ?`), and
 *  a stay with NEITHER date returns null so the top-sub drops entirely. */
export function stayRange(checkIn: string | null | undefined, checkOut: string | null | undefined): string | null {
  if (!checkIn && !checkOut) return null;
  return `${checkIn ? shortDate(checkIn) : "?"} → ${checkOut ? shortDate(checkOut) : "?"}`;
}
